// client/src/components/command/SetupTab.jsx
// Configuration form for the Command module — credentials, location, tags.

import React, { useState, useEffect } from "react";
import { useConfig, useLocations, useShopifyScopes } from "../../hooks/useCommand";

const s = {
    container: { maxWidth: "720px", margin: "0 auto" },
    section: {
        background: "linear-gradient(135deg, #1a1f2e 0%, #0d1117 100%)",
        borderRadius: "12px",
        padding: "24px",
        border: "1px solid rgba(139, 92, 246, 0.1)",
        marginBottom: "16px",
    },
    sectionTitle: { color: "#e2e8f0", fontSize: "16px", fontWeight: 700, marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" },
    row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" },
    label: { color: "#94a3b8", fontSize: "12px", fontWeight: 600, marginBottom: "4px", display: "block" },
    input: {
        width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid rgba(139, 92, 246, 0.2)",
        background: "#0d1117", color: "#e2e8f0", fontSize: "13px", outline: "none", boxSizing: "border-box",
    },
    textarea: {
        width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid rgba(139, 92, 246, 0.2)",
        background: "#0d1117", color: "#e2e8f0", fontSize: "13px", outline: "none", minHeight: "80px", resize: "vertical", boxSizing: "border-box",
        fontFamily: "inherit",
    },
    select: {
        width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid rgba(139, 92, 246, 0.2)",
        background: "#0d1117", color: "#e2e8f0", fontSize: "13px", outline: "none", boxSizing: "border-box",
    },
    btn: (primary) => ({
        padding: "10px 24px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "14px",
        background: primary ? "linear-gradient(135deg, #8b5cf6, #6366f1)" : "rgba(255,255,255,0.06)",
        color: primary ? "#fff" : "#94a3b8",
        opacity: primary ? 1 : 0.8,
    }),
    btnRow: { display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px" },
    status: (ok) => ({
        background: ok ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
        color: ok ? "#10b981" : "#ef4444",
        padding: "10px 16px", borderRadius: "8px", fontSize: "13px", marginTop: "12px",
    }),
    scopeBar: { display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", flexWrap: "wrap" },
    scopeBadge: (tone) => ({
        padding: "6px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 700,
        border: tone === "ok"
            ? "1px solid rgba(16,185,129,0.35)"
            : tone === "warn"
                ? "1px solid rgba(245,158,11,0.35)"
                : "1px solid rgba(239,68,68,0.35)",
        color: tone === "ok" ? "#10b981" : tone === "warn" ? "#f59e0b" : "#ef4444",
        background: tone === "ok"
            ? "rgba(16,185,129,0.1)"
            : tone === "warn"
                ? "rgba(245,158,11,0.1)"
                : "rgba(239,68,68,0.1)",
    }),
    scopeText: { color: "#94a3b8", fontSize: "12px", lineHeight: 1.5, marginTop: "8px" },
};

export default function SetupTab() {
    const { config, loading, saving, error, save } = useConfig();
    const { locations, loading: locLoading, fetchLocations } = useLocations();
    const { scopes, loading: scopesLoading, error: scopesError, refresh: refreshScopes } = useShopifyScopes();
    const [form, setForm] = useState({});
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (config) setForm({ ...config });
    }, [config]);

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        const ok = await save(form);
        if (ok) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
    };

    const handleFetchLocations = async () => {
        await fetchLocations();
    };

    const scopeTone = !scopes
        ? "warn"
        : scopes.canUseGraphqlValidation && scopes.hasFullFulfillmentCoverage
            ? "ok"
            : scopes.canUseGraphqlValidation
                ? "warn"
                : "bad";

    const scopeLabel = !scopes
        ? "Scope status unknown"
        : scopes.canUseGraphqlValidation && scopes.hasFullFulfillmentCoverage
            ? "GraphQL validation ready (full coverage)"
            : scopes.canUseGraphqlValidation
                ? "GraphQL ready (partial fulfillment coverage)"
                : "GraphQL validation not ready";

    if (loading) return <div style={{ color: "#94a3b8", padding: "40px", textAlign: "center" }}>Loading configuration…</div>;

    return (
        <div style={s.container}>
            {/* ShipHero Credentials */}
            <div style={s.section}>
                <div style={s.sectionTitle}>🔑 ShipHero Credentials</div>
                <div style={s.row}>
                    <div>
                        <label style={s.label}>API Token</label>
                        <input style={s.input} type="password" value={form.shiphero_token || ""} onChange={e => set("shiphero_token", e.target.value)} placeholder="Bearer token" />
                    </div>
                    <div>
                        <label style={s.label}>Account ID</label>
                        <input style={s.input} value={form.account_id || ""} onChange={e => set("account_id", e.target.value)} placeholder="Optional" />
                    </div>
                </div>
                <div>
                    <label style={s.label}>Warehouse ID</label>
                    <input style={s.input} value={form.warehouse_id || ""} onChange={e => set("warehouse_id", e.target.value)} placeholder="Filter orders by warehouse" />
                </div>
            </div>

            {/* Shopify Credentials */}
            <div style={s.section}>
                <div style={s.sectionTitle}>🏪 Shopify Credentials</div>
                <div style={s.row}>
                    <div>
                        <label style={s.label}>Store URL</label>
                        <input style={s.input} value={form.shopify_store || ""} onChange={e => set("shopify_store", e.target.value)} placeholder="your-store.myshopify.com" />
                    </div>
                    <div>
                        <label style={s.label}>Access Token</label>
                        <input style={s.input} type="password" value={form.shopify_token || ""} onChange={e => set("shopify_token", e.target.value)} placeholder="shpat_xxxxx" />
                    </div>
                </div>
                <div style={s.scopeBar}>
                    <button style={s.btn(false)} onClick={refreshScopes} disabled={scopesLoading}>
                        {scopesLoading ? "Checking permissions..." : "Check Shopify Permissions"}
                    </button>
                    <span style={s.scopeBadge(scopeTone)}>{scopeLabel}</span>
                </div>
                {scopes && (
                    <div style={s.scopeText}>
                        <div>Detection: {scopes.scopeDetection || "unknown"}</div>
                        {Array.isArray(scopes.missingRequired) && scopes.missingRequired.length > 0 && (
                            <div>Missing required: {scopes.missingRequired.join(", ")}</div>
                        )}
                        {Array.isArray(scopes.missingCoverageScopes) && scopes.missingCoverageScopes.length > 0 && (
                            <div>Missing fulfillment coverage: {scopes.missingCoverageScopes.join(", ")}</div>
                        )}
                        {Array.isArray(scopes.missingRecommended) && scopes.missingRecommended.length > 0 && (
                            <div>Missing recommended: {scopes.missingRecommended.join(", ")}</div>
                        )}
                    </div>
                )}
                {scopesError && <div style={{ ...s.scopeText, color: "#ef4444" }}>Scope check error: {scopesError}</div>}
            </div>

            {/* Location Settings */}
            <div style={s.section}>
                <div style={s.sectionTitle}>📍 Location Settings</div>
                <div style={{ marginBottom: "12px" }}>
                    <button style={s.btn(false)} onClick={handleFetchLocations} disabled={locLoading}>
                        {locLoading ? "Fetching…" : "🔄 Fetch Shopify Locations"}
                    </button>
                </div>
                {locations.length > 0 && (
                    <div style={{ marginBottom: "12px" }}>
                        <label style={s.label}>Select Expected Location</label>
                        <select style={s.select} value={form.expected_location_id || ""} onChange={e => {
                            const loc = locations.find(l => l.id === e.target.value);
                            set("expected_location_id", e.target.value);
                            if (loc) set("expected_location_name", loc.name);
                        }}>
                            <option value="">— Select —</option>
                            {locations.map(l => <option key={l.id} value={l.id}>{l.name} ({l.id})</option>)}
                        </select>
                    </div>
                )}
                <div style={s.row}>
                    <div>
                        <label style={s.label}>Expected Location ID</label>
                        <input style={s.input} value={form.expected_location_id || ""} onChange={e => set("expected_location_id", e.target.value)} placeholder="Enter or select above" />
                    </div>
                    <div>
                        <label style={s.label}>Location Name</label>
                        <input style={s.input} value={form.expected_location_name || ""} onChange={e => set("expected_location_name", e.target.value)} placeholder="Descriptive name" />
                    </div>
                </div>
            </div>

            {/* Display Timezone */}
            <div style={s.section}>
                <div style={s.sectionTitle}>🕐 Display Timezone</div>
                <div style={s.row}>
                    <div>
                        <label style={s.label}>Timezone</label>
                        <select style={s.select} value={form.display_timezone || "America/New_York"} onChange={e => set("display_timezone", e.target.value)}>
                            <option value="UTC">UTC (ShipHero API native)</option>
                            <option value="America/New_York">America/New_York — Eastern (ET)</option>
                            <option value="America/Chicago">America/Chicago — Central (CT)</option>
                            <option value="America/Denver">America/Denver — Mountain (MT)</option>
                            <option value="America/Los_Angeles">America/Los_Angeles — Pacific (PT)</option>
                            <option value="America/Phoenix">America/Phoenix — Arizona (MST, no DST)</option>
                            <option value="America/Anchorage">America/Anchorage — Alaska (AKT)</option>
                            <option value="Pacific/Honolulu">Pacific/Honolulu — Hawaii (HT)</option>
                            <option value="Europe/London">Europe/London — GMT/BST</option>
                            <option value="Europe/Paris">Europe/Paris — CET/CEST</option>
                            <option value="Asia/Dubai">Asia/Dubai — GST (UTC+4)</option>
                            <option value="Asia/Kolkata">Asia/Kolkata — IST (UTC+5:30)</option>
                            <option value="Asia/Tokyo">Asia/Tokyo — JST (UTC+9)</option>
                            <option value="Asia/Shanghai">Asia/Shanghai — CST (UTC+8)</option>
                            <option value="Australia/Sydney">Australia/Sydney — AEST/AEDT</option>
                        </select>
                        <div style={{ color: "#64748b", fontSize: "11px", marginTop: 4 }}>
                            Applies to CSV exports and date display in the UI. ShipHero API timestamps are stored in UTC.
                        </div>
                    </div>
                </div>
            </div>

            {/* Tags & Concurrency */}
            <div style={s.section}>
                <div style={s.sectionTitle}>🏷️ Tags & Concurrency</div>
                <div style={s.row}>
                    <div>
                        <label style={s.label}>Cancel Tag</label>
                        <input style={s.input} value={form.cancel_tag || ""} onChange={e => set("cancel_tag", e.target.value)} placeholder="SH-LOCATION-MISMATCH" />
                    </div>
                    <div>
                        <label style={s.label}>Additional Tags (one per line)</label>
                        <textarea style={s.textarea} value={form.additional_tags || ""} onChange={e => set("additional_tags", e.target.value)} placeholder="One tag per line" />
                    </div>
                </div>
                <div style={{ ...s.row, gridTemplateColumns: "1fr 1fr 1fr" }}>
                    <div>
                        <label style={s.label}>Fetch Concurrency</label>
                        <input style={s.input} type="number" min="1" max="10" value={form.fetch_concurrency || 3} onChange={e => set("fetch_concurrency", parseInt(e.target.value) || 3)} />
                    </div>
                    <div>
                        <label style={s.label}>Validation Concurrency</label>
                        <input style={s.input} type="number" min="1" max="20" value={form.validation_concurrency || 8} onChange={e => set("validation_concurrency", parseInt(e.target.value) || 8)} />
                    </div>
                    <div>
                        <label style={s.label}>Action Concurrency</label>
                        <input style={s.input} type="number" min="1" max="10" value={form.action_concurrency || 5} onChange={e => set("action_concurrency", parseInt(e.target.value) || 5)} />
                    </div>
                </div>
            </div>

            <div style={s.btnRow}>
                <button style={s.btn(true)} onClick={handleSave} disabled={saving}>
                    {saving ? "Saving…" : "💾 Save Configuration"}
                </button>
            </div>

            {saved && <div style={s.status(true)}>✓ Configuration saved successfully</div>}
            {error && <div style={s.status(false)}>✗ {error}</div>}
        </div>
    );
}
